import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, MapPin, Sparkles, Truck } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";

const EVENT_DATE = new Date("2026-10-17T10:00:00-05:00"); // Langston, OK — Oct 17

const useCountdown = (target) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds, isPast: diff === 0 };
};

const CountdownUnit = ({ label, value }) => (
  <div
    data-testid={`countdown-${label.toLowerCase()}`}
    className="flex flex-col items-center bg-[#0B2453] text-white px-5 py-4 min-w-[76px] border border-[#F58221]"
  >
    <div className="font-display font-black text-3xl md:text-4xl leading-none tabular-nums">
      {String(value).padStart(2, "0")}
    </div>
    <div className="text-[9px] tracking-[0.28em] uppercase mt-1 text-[#F58221]">{label}</div>
  </div>
);

const Homecoming = () => {
  const [products, setProducts] = useState([]);
  const { days, hours, minutes, seconds, isPast } = useCountdown(EVENT_DATE);

  useEffect(() => {
    api
      .get("/products", { params: { category: "hbcu" } })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]));
  }, []);

  const featured = useMemo(() => products.slice(0, 8), [products]);

  return (
    <main data-testid="homecoming-page" className="bg-white">
      {/* Hero */}
      <section className="relative bg-[#0B2453] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #F58221 0px, transparent 40%), radial-gradient(circle at 80% 70%, #EC008C 0px, transparent 45%)" }} />
        <div className="container-x relative py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-[11px] tracking-[0.32em] uppercase text-[#F58221] font-black mb-4"
            >
              Langston University · Homecoming
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="font-display font-black text-5xl md:text-7xl leading-[0.95] tracking-tight"
            >
              Rep your <span className="text-[#F58221]">Pride.</span>
              <span className="block">Wear the <em className="not-italic text-[#F58221]">Legacy.</em></span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.5 }}
              className="mt-6 text-base md:text-lg text-white/90 max-w-lg font-light leading-relaxed"
            >
              Officially licensed Langston University merch — designed and printed in Oklahoma.
              <strong className="text-white"> 5% of every sale supports the Langston University Foundation.</strong>
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.5 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link
                to="/shop?category=hbcu"
                data-testid="homecoming-cta-shop"
                className="inline-flex items-center gap-2 bg-[#F58221] text-white px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-[#F58221] hover:bg-white hover:text-[#0B2453] hover:border-white transition"
              >
                Shop Langston Merch <ArrowRight size={14} />
              </Link>
              <a
                href="#collection"
                data-testid="homecoming-cta-scroll"
                className="inline-flex items-center gap-2 bg-transparent text-white px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-white hover:bg-white hover:text-[#0B2453] transition"
              >
                See the drop
              </a>
            </motion.div>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-white/80">
              <span className="flex items-center gap-2"><Calendar size={13} /> Homecoming · Oct 17</span>
              <span className="flex items-center gap-2"><MapPin size={13} /> Langston, OK</span>
              <span className="flex items-center gap-2"><Sparkles size={13} /> Live printing on-site</span>
            </div>
          </div>

          {/* Countdown */}
          <div className="flex flex-col items-start md:items-end">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#F58221] font-black mb-4">
              {isPast ? "It's Homecoming Week" : "Countdown to Homecoming"}
            </div>
            <div data-testid="homecoming-countdown" className="flex gap-2 flex-wrap">
              <CountdownUnit label="Days" value={days} />
              <CountdownUnit label="Hours" value={hours} />
              <CountdownUnit label="Min" value={minutes} />
              <CountdownUnit label="Sec" value={seconds} />
            </div>
            <img
              src="/hero/lu-homecoming.webp"
              alt="Langston University Homecoming banner"
              className="mt-8 w-full max-w-md border-2 border-[#F58221] shadow-2xl"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Impact strip */}
      <section className="border-y border-black bg-white">
        <div className="container-x py-6 grid md:grid-cols-3 gap-4 items-center">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#F58221] text-white flex items-center justify-center font-black">5%</div>
            <div className="text-xs">
              <div className="uppercase tracking-widest text-[10px] font-black text-[#0B2453]">Legacy Give-Back</div>
              <div className="text-[#333]">Supports Langston University Foundation</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Truck size={20} className="text-[#0B2453]" />
            <div className="text-xs">
              <div className="uppercase tracking-widest text-[10px] font-black text-[#0B2453]">$10.99 Flat Shipping</div>
              <div className="text-[#333]">Anywhere in the US</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Sparkles size={20} className="text-[#0B2453]" />
            <div className="text-xs">
              <div className="uppercase tracking-widest text-[10px] font-black text-[#0B2453]">Officially Licensed</div>
              <div className="text-[#333]">MAKEBA × Langston U</div>
            </div>
          </div>
        </div>
      </section>

      {/* Product collection */}
      <section id="collection" data-testid="homecoming-collection" className="container-x py-16 md:py-24 scroll-mt-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#F58221] font-black mb-3">The Collection</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight text-black">
              Langston Homecoming <em className="not-italic text-[#F58221]">Drop.</em>
            </h2>
          </div>
          <Link
            to="/shop?category=hbcu"
            className="text-[11px] tracking-[0.22em] uppercase font-semibold text-black underline underline-offset-4 hover:text-[#F58221]"
          >
            View all Langston merch →
          </Link>
        </div>
        {featured.length === 0 ? (
          <div className="text-center py-16 border border-[#E5E2DA]">
            <p className="text-[#4A4A4A]">Homecoming collection loading — check back shortly.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* Foundation callout */}
      <section className="bg-[#0B2453] text-white">
        <div className="container-x py-16 text-center max-w-3xl mx-auto">
          <div className="text-[11px] tracking-[0.32em] uppercase text-[#F58221] font-black mb-4">Legacy Give-Back</div>
          <h3 className="font-display font-black text-3xl md:text-5xl leading-tight tracking-tight">
            Every tee funds the next <em className="not-italic text-[#F58221]">Lion.</em>
          </h3>
          <p className="mt-5 text-white/85 leading-relaxed">
            5% of every Langston Homecoming purchase is donated to the Langston University Foundation —
            supporting scholarships, student programs, and the legacy of Oklahoma's only HBCU.
          </p>
          <Link
            to="/shop?category=hbcu"
            data-testid="homecoming-cta-shop-bottom"
            className="mt-8 inline-flex items-center gap-2 bg-[#F58221] text-white px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-[#F58221] hover:bg-white hover:text-[#0B2453] hover:border-white transition"
          >
            Shop the Homecoming Drop <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Homecoming;
